export type ListSortValue = string | number | null;
export type ListSortDirection = 'asc' | 'desc';

export interface ListSortRule<TKey extends string> {
  key: TKey;
  direction: ListSortDirection;
}

const compareListValues = (
  leftValue: ListSortValue,
  rightValue: ListSortValue,
  direction: ListSortDirection
) => {
  // Missing values should remain at the end regardless of direction.
  if (leftValue === null && rightValue === null) return 0;
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;

  const result =
    typeof leftValue === 'number' && typeof rightValue === 'number'
      ? leftValue - rightValue
      : String(leftValue).localeCompare(String(rightValue), undefined, {
          numeric: true,
          sensitivity: 'base',
        });

  return direction === 'asc' ? result : -result;
};

export const sortListItems = <T>(
  items: readonly T[],
  direction: ListSortDirection,
  getValue: (item: T) => ListSortValue
): T[] =>
  [...items].sort((left, right) => {
    return compareListValues(getValue(left), getValue(right), direction);
  });

export const sortListItemsByRules = <T, TKey extends string>(
  items: readonly T[],
  rules: readonly ListSortRule<TKey>[],
  getValue: (item: T, key: TKey) => ListSortValue
): T[] => {
  if (rules.length === 0) return [...items];

  return [...items].sort((left, right) => {
    for (const rule of rules) {
      const result = compareListValues(
        getValue(left, rule.key),
        getValue(right, rule.key),
        rule.direction
      );
      if (result !== 0) return result;
    }

    return 0;
  });
};
